import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Spinner,
  Form,
} from "react-bootstrap";
import { useCookies } from "react-cookie";
import {
  fetchClassSections,
  fetchAcademicyears,
  fetchAttendanceHistory,
  getUsers,
} from "ServerAPI/serverAPI";
import { useToast } from "context/toast-context/ToastProvider";
import { Users, Calendar, BarChart2, TrendingUp } from "react-feather";

const AttendanceHistory = () => {
  const [cookies] = useCookies();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState("");
  const [attendanceStats, setAttendanceStats] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [sectionSummary, setSectionSummary] = useState(null);

  // Fetch sections and users on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const payload = { userId: cookies.userId };
        const [sectionsRes, academicYearRes, usersRes] = await Promise.all([
          fetchClassSections(payload),
          fetchAcademicyears(payload),
          getUsers(cookies.email, cookies.roleId, cookies.organizationId),
        ]);

        if (sectionsRes?.status === "success" && academicYearRes?.status === "success") {
          const currentYear = academicYearRes?.data?.find(a => a.isCurrent === true)?.academicYearId;
          const filteredSections = sectionsRes?.data?.filter(c => c.academicYear === currentYear) || [];
          setSections(filteredSections);
          setAllUsers(usersRes?.data?.users || []);
        } else {
          showToast("Failed to fetch initial data", "error");
        }
      } catch (error) {
        showToast("Error loading data", "error");
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [cookies.userId]);

  // Fetch attendance history when section changes
  useEffect(() => {
    if (!sectionId) {
      setAttendanceStats([]);
      setSectionSummary(null);
      return;
    }

    const loadAttendance = async () => {
      setLoading(true);
      try {
        const response = await fetchAttendanceHistory({
          classSectionId: sectionId,
          organizationId: cookies.organizationId,
        });

        if (response?.status === "success") {
          const stats = response.data?.students || [];
          // Map user names from allUsers
          const userMap = {};
          allUsers.forEach(u => { userMap[u.userId] = u; });

          const enriched = stats.map(s => ({
            ...s,
            name: userMap[s.studentId]?.name || "Unknown",
            email: userMap[s.studentId]?.email || "—",
          }));

          // Sort by attendance percentage ascending (lowest first)
          enriched.sort((a, b) => a.percentage - b.percentage);

          setAttendanceStats(enriched);
          setSectionSummary(response.data?.sectionSummary || null);
        } else {
          setAttendanceStats([]);
          setSectionSummary(null);
          showToast(response?.message || "No attendance data found", "info");
        }
      } catch (error) {
        showToast("Error fetching attendance history", "error");
      } finally {
        setLoading(false);
      }
    };
    loadAttendance();
  }, [sectionId, allUsers]);

  const currentSection = sections.find(s => s.classSectionId === sectionId);

  const getPercentageBadge = (pct) => {
    if (pct >= 90) return "success";
    if (pct >= 75) return "primary";
    if (pct >= 60) return "warning";
    return "danger";
  };

  const getProgressBarColor = (pct) => {
    if (pct >= 90) return "#198754";
    if (pct >= 75) return "#0d6efd";
    if (pct >= 60) return "#ffc107";
    return "#dc3545";
  };

  return (
    <Container fluid className="p-4 bg-light min-vh-100">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between bg-white p-3 rounded shadow-sm border-start border-primary border-5">
            <div>
              <h2 className="mb-0 fw-bold text-dark">
                <BarChart2 size={28} className="me-2 text-primary" />
                Attendance History
              </h2>
              <p className="text-muted mb-0 mt-1">View student-wise attendance percentages for each section.</p>
            </div>
            <Badge bg="primary" className="p-2 fs-6">
              <Calendar size={16} className="me-2" />
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Badge>
          </div>
        </Col>
      </Row>

      {/* Section Selector */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Form.Label className="fw-bold small text-uppercase text-muted">Select Section</Form.Label>
              <Form.Select
                value={sectionId}
                onChange={(e) => setSectionId(e.target.value)}
                className="border-primary"
              >
                <option value="">-- Choose a Section --</option>
                {sections.map(s => (
                  <option key={s.classSectionId} value={s.classSectionId}>
                    {s.title}
                  </option>
                ))}
              </Form.Select>
            </Card.Body>
          </Card>
        </Col>

        {/* Section Summary Cards */}
        {sectionSummary && (
          <>
            <Col md={2}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center d-flex flex-column justify-content-center">
                  <div className="text-muted small fw-bold text-uppercase">Total Slots</div>
                  <div className="h3 mb-0 fw-bold text-primary">{sectionSummary.totalSlots}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="text-center d-flex flex-column justify-content-center">
                  <div className="text-muted small fw-bold text-uppercase">Students</div>
                  <div className="h3 mb-0 fw-bold">{sectionSummary.totalStudents}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 shadow-sm h-100" style={{ backgroundColor: 'rgba(25,135,84,0.05)' }}>
                <Card.Body className="text-center d-flex flex-column justify-content-center">
                  <div className="text-success small fw-bold text-uppercase">Avg Attendance</div>
                  <div className="h3 mb-0 fw-bold text-success">{sectionSummary.avgPercentage}%</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="border-0 shadow-sm h-100" style={{ backgroundColor: 'rgba(220,53,69,0.05)' }}>
                <Card.Body className="text-center d-flex flex-column justify-content-center">
                  <div className="text-danger small fw-bold text-uppercase">Below 75%</div>
                  <div className="h3 mb-0 fw-bold text-danger">{sectionSummary.belowThreshold}</div>
                </Card.Body>
              </Card>
            </Col>
          </>
        )}
      </Row>

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Loading attendance data...</p>
        </div>
      )}

      {/* Student Attendance Table */}
      {!loading && sectionId && attendanceStats.length > 0 && (
        <Row>
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold">
                    <Users size={18} className="me-2" />
                    Student Attendance — <span className="text-primary">{currentSection?.title}</span>
                  </h5>
                  <Badge bg="secondary" className="px-3 py-2">
                    {attendanceStats.length} Students
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  <Table hover responsive className="mb-0 align-middle">
                    <thead className="bg-light text-uppercase small fw-bold" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th className="ps-4 py-3 text-muted" style={{ width: '60px' }}>S.No</th>
                        <th className="py-3">Student Name</th>
                        <th className="py-3">Email</th>
                        <th className="py-3 text-center" style={{ width: '100px' }}>Present</th>
                        <th className="py-3 text-center" style={{ width: '100px' }}>Absent</th>
                        <th className="py-3 text-center" style={{ width: '100px' }}>Total</th>
                        <th className="py-3" style={{ width: '250px' }}>Attendance %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceStats.map((student, index) => (
                        <tr key={student.studentId}>
                          <td className="ps-4 text-muted font-monospace">{index + 1}</td>
                          <td className="fw-semibold">{student.name}</td>
                          <td className="text-muted small">{student.email}</td>
                          <td className="text-center">
                            <span className="text-success fw-bold">{student.present}</span>
                          </td>
                          <td className="text-center">
                            <span className="text-danger fw-bold">{student.absent}</span>
                          </td>
                          <td className="text-center">
                            <span className="fw-bold">{student.total}</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="flex-grow-1" style={{ height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    width: `${student.percentage}%`,
                                    height: '100%',
                                    backgroundColor: getProgressBarColor(student.percentage),
                                    borderRadius: '4px',
                                    transition: 'width 0.5s ease',
                                  }}
                                />
                              </div>
                              <Badge
                                bg={getPercentageBadge(student.percentage)}
                                className="px-2 py-1"
                                style={{ minWidth: '50px', fontSize: '11px' }}
                              >
                                {student.percentage}%
                              </Badge>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Empty state */}
      {!loading && sectionId && attendanceStats.length === 0 && (
        <Row>
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5">
                <TrendingUp size={48} className="mb-3 text-muted opacity-25" />
                <h5 className="text-muted">No Attendance Records</h5>
                <p className="text-muted small">No attendance has been marked for this section yet.</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* No section selected */}
      {!loading && !sectionId && (
        <Row>
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5">
                <BarChart2 size={48} className="mb-3 text-muted opacity-25" />
                <h5 className="text-muted">Select a Section</h5>
                <p className="text-muted small">Choose a section from the dropdown above to view attendance history.</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default AttendanceHistory;
